import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });

/** Print-ready one-page personal itinerary (black & white). */
export default function PrintItinerary() {
  const { user } = useAuth();
  const [it, setIt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/matchmaking/me/itinerary")
      .then(({ data }) => setIt(data))
      .catch(() => setErr("Sign in first to print your itinerary."));
  }, []);

  const meetings = [
    ...(it?.appointments || []).map((a) => ({
      start: a.start_iso, end: a.end_iso, kind: "Matchmaking",
      label: a.round_label,
      who: it.role === "prime" ? (a.sbe?.company || a.sbe?.name || a.sbe_company || a.sbe_name || "Open slot") : (a.prime_supplier?.company_name || "Prime Supplier"),
      where: `Matchmaking Table M-${a.table_number}`,
    })),
    ...(it?.tl_meetings || []).map((m) => ({
      start: m.start_iso, end: m.end_iso, kind: "T+L Sourcing",
      who: it.role === "tl" ? (m.prime_supplier?.company_name || m.sbe?.company || m.sbe_name || "Participant") : `${m.tl_leader_name || "Travel + Leisure"} · Travel + Leisure`,
      where: m.location,
    })),
  ].sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));

  const roleLabel = { prime: "Prime Supplier", sbe: "Small Business Enterprise", tl: "Travel + Leisure Sourcing" }[it?.role] || "Attendee";

  return (
    <div className="min-h-screen bg-white text-black" data-testid="print-itinerary-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>

      <div className="no-print border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <Link to="/matchmaking" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black">
          <ArrowLeft size={14} /> Back to Matchmaking
        </Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-black text-white px-5 h-10 text-xs uppercase tracking-[0.18em]" data-testid="print-now-btn">
          <Printer size={14} /> Print / Save PDF
        </button>
      </div>

      <div className="max-w-[7.5in] mx-auto px-8 py-10">
        {err && <p className="text-sm">{err} <Link to="/login" className="underline">Sign in</Link></p>}
        {it && (
          <>
            <div className="border-b-2 border-black pb-4">
              <div className="text-[10px] uppercase tracking-[0.3em]">Business Matchmaking Itinerary</div>
              <h1 style={{ fontFamily: "Georgia, serif" }} className="text-2xl mt-2">Travel + Leisure Strategic Supplier Summit</h1>
              <div className="text-sm mt-1">Friday, September 11, 2026 · Travel + Leisure Headquarters, Orlando</div>
            </div>
            <div className="mt-4 flex items-baseline justify-between">
              <div>
                <div className="text-lg font-semibold">{user?.name || user?.email}</div>
                <div className="text-sm">{roleLabel}</div>
              </div>
              <div className="text-xs text-right">
                Matchmaking window · 1:00 – 3:30 PM ET<br />Open Collaboration · 3:00 – 3:30 PM
              </div>
            </div>

            {meetings.length === 0 ? (
              <p className="mt-8 text-sm" data-testid="print-empty">
                No formal matchmaking meetings are currently scheduled for you. You are invited to visit the Supplier Showcase and participate in open networking throughout the event.
              </p>
            ) : (
              <table className="w-full mt-6 text-sm border-collapse" data-testid="print-meetings-table">
                <thead>
                  <tr className="border-b border-black text-left">
                    <th className="py-2 pr-3 font-semibold w-[1.7in]">Time (ET)</th>
                    <th className="py-2 pr-3 font-semibold">Meeting With</th>
                    <th className="py-2 pr-3 font-semibold w-[2in]">Location</th>
                    <th className="py-2 font-semibold w-[1.1in]">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((m, i) => (
                    <tr key={i} className="border-b border-zinc-300 align-top">
                      <td className="py-2 pr-3 tabular-nums">{m.start ? `${fmt(m.start)} – ${fmt(m.end)}` : m.label || "—"}</td>
                      <td className="py-2 pr-3 font-medium">{m.who}</td>
                      <td className="py-2 pr-3">{m.where}</td>
                      <td className="py-2 text-xs uppercase tracking-wide">{m.kind}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="mt-10 pt-3 border-t border-zinc-300 text-[11px] leading-relaxed">
              Keep this schedule available when you arrive. Check in at the Business Matchmaking Check-In Table inside the Supplier Showcase at least five minutes before your meeting ·
              Free parking at the 501 W. Church St. Garage · Questions: info@displayamerica.com
            </div>
          </>
        )}
      </div>
    </div>
  );
}
